import {
  ChangeDetectionStrategy,
  Component,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { formatChf } from 'src/app/_helpers/currency';
import { EMPTY_VALUE, fmtDate } from 'src/app/_helpers/utils';
import {
  FlowInvoiceInsight,
  getBillingDocumentTypeLabel,
  getBillingItemDocumentType,
  getBillStateLabel,
  getFlowInvoiceReason,
  getInvoiceActorName,
} from 'src/app/_types/billing.types';
import { getDeliveryMethodLabel } from 'src/app/Dashboard/billing/invoice-comparator-modal/comparison-form/delivery-method-select/delivery-method-select.component';

import { FlowInvoicesGridContext } from '../flow-invoices-grid-context';
import { FlowInvoiceDocumentRendererComponent } from '../renderers/flow-invoice-document-renderer.component';
import { FlowInvoiceGapRendererComponent } from '../renderers/flow-invoice-gap-renderer.component';
import { FlowInvoicePeriodRendererComponent } from '../renderers/flow-invoice-period-renderer.component';
import { FlowInvoiceReasonRendererComponent } from '../renderers/flow-invoice-reason-renderer.component';
import { FlowInvoiceStatusRendererComponent } from '../renderers/flow-invoice-status-renderer.component';
import { FlowInvoiceTypeRendererComponent } from '../renderers/flow-invoice-type-renderer.component';

/**
 * One flow's invoices. The section renders one of these per insurance flow, so
 * the grid sizes itself (`autoHeight`) and carries no empty state — a group only
 * exists because it has rows.
 */
@Component({
  selector: 'app-flow-invoices-table',
  standalone: true,
  imports: [AgGridModule],
  templateUrl: './flow-invoices-table.component.html',
  // Cell styles live in the parent and must reach the AG Grid DOM
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowInvoicesTableComponent {
  readonly rows = input.required<FlowInvoiceInsight[]>();

  readonly context = input.required<FlowInvoicesGridContext>();

  readonly defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: false,
    flex: 1,
    minWidth: 120,
  };

  readonly columnDefs: ColDef<FlowInvoiceInsight>[] = [
    {
      colId: 'id',
      headerName: $localize`ID`,
      field: 'displayId',
      minWidth: 180,
      valueFormatter: (p: ValueFormatterParams) => p.value ?? EMPTY_VALUE,
    },
    {
      colId: 'originalInvoice',
      headerName: $localize`Facture`,
      minWidth: 150,
      valueGetter: (p) =>
        p.data?.kind === 'REMINDER'
          ? (p.data.originalInvoiceDisplayId ?? p.data.invoiceNo ?? EMPTY_VALUE)
          : EMPTY_VALUE,
    },
    {
      colId: 'type',
      headerName: $localize`Type`,
      minWidth: 150,
      valueGetter: (p) => {
        const type = p.data ? getBillingItemDocumentType(p.data) : null;
        return type ? getBillingDocumentTypeLabel(type) : EMPTY_VALUE;
      },
      cellRenderer: FlowInvoiceTypeRendererComponent,
    },
    {
      colId: 'period',
      headerName: $localize`Période`,
      field: 'periodStart',
      minWidth: 175,
      cellRenderer: FlowInvoicePeriodRendererComponent,
    },
    {
      colId: 'receptionDate',
      headerName: $localize`Date réception`,
      field: 'receptionDate',
      minWidth: 150,
      valueFormatter: (p: ValueFormatterParams) => fmtDate(p.value),
    },
    {
      colId: 'amount',
      headerName: $localize`Prime facture`,
      field: 'amount',
      minWidth: 150,
      valueFormatter: (p: ValueFormatterParams) => formatChf(p.value ?? 0),
    },
    {
      colId: 'policyPremium',
      headerName: $localize`Prime LTY`,
      field: 'policyPremium',
      minWidth: 135,
      valueFormatter: (p: ValueFormatterParams) =>
        p.value != null ? formatChf(p.value) : EMPTY_VALUE,
    },
    {
      colId: 'premiumGap',
      headerName: $localize`Écart`,
      field: 'premiumGap',
      minWidth: 200,
      cellRenderer: FlowInvoiceGapRendererComponent,
    },
    {
      colId: 'gapReason',
      headerName: $localize`Motif`,
      minWidth: 199,
      valueGetter: (p) => (p.data ? getFlowInvoiceReason(p.data).label : ''),
      cellRenderer: FlowInvoiceReasonRendererComponent,
    },
    {
      colId: 'status',
      headerName: $localize`Statut`,
      field: 'status',
      minWidth: 130,
      valueGetter: (p) => (p.data ? getBillStateLabel(p.data.status) : ''),
      cellRenderer: FlowInvoiceStatusRendererComponent,
    },
    {
      colId: 'communicationMethod',
      headerName: $localize`Envoi`,
      minWidth: 105,
      valueGetter: (p) =>
        p.data?.communicationMethod
          ? getDeliveryMethodLabel(p.data.communicationMethod)
          : EMPTY_VALUE,
    },
    {
      colId: 'processedBy',
      headerName: $localize`Traité par`,
      minWidth: 130,
      cellClass: 'flow-invoice-processed-by',
      valueGetter: (p) =>
        p.data?.processedBy
          ? getInvoiceActorName(p.data.processedBy)
          : EMPTY_VALUE,
    },
    {
      colId: 'document',
      headerName: '',
      minWidth: 64,
      maxWidth: 64,
      flex: 0,
      sortable: false,
      resizable: false,
      cellRenderer: FlowInvoiceDocumentRendererComponent,
    },
  ];

  readonly getRowId = (params: { data: FlowInvoiceInsight }): string =>
    params.data.id;
}
